import { useTranslation } from 'react-i18next';

import type { ChampionMasterySummary, ChampionMasteryEntry } from '@/types/qriots';

interface MasteryDeepDiveProps {
  data: ChampionMasterySummary;
}

function formatPoints(p: number): string {
  if (p >= 1000000) return `${(p / 1000000).toFixed(1)}M`;
  if (p >= 1000) return `${(p / 1000).toFixed(0)}k`;
  return p.toString();
}

function MasteryRow({ m }: { m: ChampionMasteryEntry }) {
  const { t } = useTranslation('riot');
  const daysAgo = m.daysSinceLastPlayed;
  const lastPlayedText =
    daysAgo === 0 ? t('mastery.today') :
    daysAgo === 1 ? t('mastery.yesterday') :
    daysAgo < 30 ? t('mastery.daysAgo', { count: daysAgo }) :
    t('mastery.monthsAgo', { count: Math.floor(daysAgo / 30) });

  return (
    <li className="flex items-center gap-3 rounded-lg border border-border bg-bg-tertiary px-3 py-2.5">
      <span aria-hidden className="text-lg leading-none">🏆</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate text-sm font-bold text-fg">
          {m.championName ?? t('champion.unknown', { id: m.championId })}
        </p>
        <p className="text-xs text-fg-muted">
          M{m.championLevel} · {t('mastery.pointsShort', { points: formatPoints(m.championPoints) })} · {lastPlayedText}
        </p>
        {m.progressPct != null && m.progressPct < 100 && (
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-bg-secondary">
            <div
              className="h-full bg-success"
              style={{ width: `${Math.max(0, Math.min(100, m.progressPct))}%` }}
            />
          </div>
        )}
      </div>
      {m.chestGranted && (
        <span aria-hidden className="text-base" title={t('mastery.chestClaimed')}>
          🎁
        </span>
      )}
    </li>
  );
}

export function MasteryDeepDive({ data }: MasteryDeepDiveProps) {
  const { t } = useTranslation('riot');

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      {/* Headline */}
      <div className="mx-4 flex flex-col gap-2 rounded-xl bg-bg-secondary p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          {t('mastery.summary')}
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-fg-muted">{t('mastery.totalPoints')}</p>
            <p className="text-base font-bold text-fg">
              {formatPoints(data.totalChampionPoints)}
            </p>
          </div>
          <div>
            <p className="text-xs text-fg-muted">{t('mastery.championsTracked')}</p>
            <p className="text-base font-bold text-fg">{data.count}</p>
          </div>
          <div>
            <p className="text-xs text-fg-muted">{t('mastery.highestLevel')}</p>
            <p className="text-base font-bold text-fg">M{data.highestLevel}</p>
          </div>
          <div>
            <p className="text-xs text-fg-muted">{t('mastery.totalMasteryLevel')}</p>
            <p className="text-base font-bold text-fg">{data.totalMasteryLevel}</p>
          </div>
        </div>
      </div>

      {/* Entries */}
      {data.entries.length === 0 ? (
        <p className="mx-4 rounded-lg bg-bg-secondary p-4 text-center text-sm text-fg-muted">
          {t('mastery.empty')}
        </p>
      ) : (
        <ul className="mx-4 flex flex-col gap-2">
          {data.entries.map((m) => (
            <MasteryRow key={m.championId} m={m} />
          ))}
        </ul>
      )}

      <p className="mx-4 text-[10px] leading-relaxed text-fg-muted">{data.disclaimer}</p>
    </div>
  );
}
